import { useMutation, useQuery } from '@tanstack/react-query';
import QRCode from 'qrcode';
import { useState } from 'react';
import { Button, ErrorNote, Spinner, ui } from '@/components/ui';
import { mfaService } from '@/services/api';
import { CodeInput } from './CodeInput';
import { RecoveryCodes } from './RecoveryCodes';
import s from './Mfa.module.css';

// Scan → type the first code → save recovery codes.
export function SetupAuthenticatorApp({
  onDone,
  onCancel,
}: {
  onDone: () => void;
  onCancel: () => void;
}) {
  const [code, setCode] = useState('');
  const setup = useQuery({
    queryKey: ['mfa', 'setup'],
    queryFn: async () => {
      const data = await mfaService.startSetup();
      return { ...data, qr: await QRCode.toDataURL(data.otpauthUrl, { margin: 1, width: 400 }) };
    },
    // A new secret each time the setup opens; never reuse a cached one.
    gcTime: 0,
    staleTime: Infinity,
    retry: false,
  });
  const confirm = useMutation({ mutationFn: () => mfaService.confirmSetup(code) });

  if (confirm.data) return <RecoveryCodes codes={confirm.data.recoveryCodes} onDone={onDone} />;
  if (setup.isPending) return <Spinner />;
  if (setup.error) return <ErrorNote error={setup.error} />;
  return (
    <form
      className={ui.stack}
      onSubmit={(e) => {
        e.preventDefault();
        confirm.mutate();
      }}
    >
      <div className={s.setup}>
        <img className={s.qr} src={setup.data.qr} alt="QR code for your authenticator app" />
        <div className={ui.stack}>
          <p>
            <strong>1.</strong> Open your authenticator app and scan this code.
          </p>
          <p className={s.apps}>
            Works with Google Authenticator, Microsoft Authenticator, Authy, 1Password, Bitwarden
            and any app that supports time-based codes.
          </p>
          <p className={ui.hint}>Can’t scan? Enter this key in the app instead:</p>
          <code className={s.secret}>{setup.data.secret}</code>
          <label className={ui.fieldLabel} htmlFor="mfa-setup-code">
            <strong>2.</strong> Enter the 6-digit code the app shows
          </label>
          <CodeInput id="mfa-setup-code" value={code} onChange={setCode} />
        </div>
      </div>
      <ErrorNote error={confirm.error} />
      <div className={ui.row}>
        <Button type="submit" disabled={confirm.isPending}>
          Turn on
        </Button>
        <Button variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
